import React, { useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import useDashboardContext from 'contexts/dashboard.context';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import FormStepper from 'components/base/form-stepper';
import AddVendorDetails from './add-vendor-details';
import AddVendorStoreItems from './add-vendor-store-items';
import useVendorContext from 'contexts/vendor.context';
import ReviewVendorDetails from './review-vendor-details';
import { VendorFormType } from 'models/vendors.model';
import { apiService } from 'services/base.service';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

export const DefaultvendorFormData: VendorFormType = {
  name: '',
  phone_number: '',
  bank_name: '',
  account_number: '',
  account_name: '',
  store_items: [],
  payment_period: ''
};

const AddVendor = () => {
  const { setIsBackLink, setBackLinkTitle, setBackLink } = useDashboardContext();
  const [isCreatingVendors, setIsCreatingVendors] = useState(false);
  const { vendorFormData, step, setStep, setVendorFormData } = useVendorContext();
  const router = useNavigate();

  function isStep1Invalid(data: VendorFormType[]): boolean {
    if (data.length === 0) return true;

    for (const vendor of data) {
      if (
        !vendor.name ||
        !vendor.phone_number ||
        !vendor.bank_name ||
        !vendor.account_number ||
        !vendor.account_name ||
        !vendor.payment_period
      ) {
        return true;
      }
    }
    return false;
  }

  function isStep3Invalid(data: VendorFormType[]): boolean {
    if (data.length === 0) return true;

    for (const vendor of data) {
      if (
        !vendor.name ||
        !vendor.phone_number ||
        !vendor.bank_name ||
        !vendor.account_number ||
        !vendor.account_name ||
        !vendor.payment_period
      ) {
        return true;
      }
    }
    return false;
  }

  function getDisabledState(): boolean {
    if (step === 1) {
      return isStep1Invalid(vendorFormData);
    } else if (step === 2) {
      return false;
    } else if (step === 3) {
      return isStep3Invalid(vendorFormData);
    }
    return true;
  }

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to vendors');
    setBackLink(MAIN_STORE_ROUTE_KEYS.VENDORS);
    return () => {
      setIsBackLink(false);
    };
  }, []);

  const parsePaymentPeriodToNumber = (value?: string) => {
    const parsed = Number(
      String(value)
        .replace(/\s*days?$/i, '')
        .trim()
    );
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const createVendor = async () => {
    const data = {
      items: vendorFormData?.map((vendorDataToUpload) => {
        return {
          name: vendorDataToUpload?.name,
          phone_number: vendorDataToUpload?.phone_number?.toString(),
          bank_name: vendorDataToUpload?.bank_name,
          account_number: vendorDataToUpload?.account_number?.toString(),
          account_name: vendorDataToUpload?.account_name,
          payment_period: parsePaymentPeriodToNumber(vendorDataToUpload?.payment_period),
          ingredients: vendorDataToUpload?.store_items?.map((store_item) => {
            return {
              ingredient_id: store_item?.ingredient_id,
              gram_price: Number(store_item?.gram_price)
            };
          })
        };
      })
    };
    try {
      setIsCreatingVendors(true);
      const res = await apiService.post('vendors/csv', { data });
      if (res) {
        router(MAIN_STORE_ROUTE_KEYS.VENDORS);
        setVendorFormData([DefaultvendorFormData]);
        toast(<ToastMessage message='Vendor(s) created successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage message={err.response.data.message || 'Error creating vendor'} type='error' />
      );
    } finally {
      setIsCreatingVendors(false);
    }
  };

  return (
    <div>
      <PageHeaderText title='Add Vendor' description='Create new vendor profile' />
      <div className={`${step == 2 ? 'max-w-[678px]' : 'max-w-full'} `}>
        <FormStepper
          disabled={getDisabledState()}
          title=''
          step={step}
          handleConfirmClick={() => createVendor()}
          handleCancelClick={() => {
            setVendorFormData([DefaultvendorFormData]);
            router(MAIN_STORE_ROUTE_KEYS.VENDORS);
          }}
          setStep={setStep}
          noOfSteps={3}
          loading={isCreatingVendors}>
          <div>
            {step === 1 ? (
              <AddVendorDetails />
            ) : step === 2 ? (
              <AddVendorStoreItems />
            ) : (
              <ReviewVendorDetails />
            )}
          </div>
        </FormStepper>
      </div>
    </div>
  );
};

export default AddVendor;
